import { Organization } from '../services/api';

/**
 * An organisation is PAYING when money is actually coming in, through one of the
 * two channels the broker-AI stack supports:
 *   - a live self-serve subscription that is active AND paid — the backend-derived
 *     `subscriptionTag === 'paid'` signal (the SAME one that draws the "Paid" pill
 *     on the Organizations list); OR
 *   - a direct EFT into the bank account — the admin-set `eftPayer` marker.
 *
 * This is the single source of truth for "is this org paying" across the Referral
 * Sources report (summary cards, source rollup, paying-org breakdown) and the FSP
 * commission. It is deliberately NOT the hand-typed `monthlyAmountZAR`, which is
 * populated on only a handful of orgs and therefore both UNDER-counts (a paid
 * subscription with no typed amount reads as non-paying) and OVER-counts (a stale
 * amount on an org that has since stopped paying). Trello #299.
 *
 * Takes the two SIGNALS rather than a full `Organization` so a Usage Health row
 * — which carries the same fields but is not an Organization — can be judged by
 * the identical predicate (Trello #377). `Organization` structurally satisfies
 * this, so every existing caller is unchanged.
 */
export function isPayingOrg(org: Pick<Organization, 'subscriptionTag' | 'eftPayer'>): boolean {
  return org.subscriptionTag === 'paid' || org.eftPayer === true;
}

/**
 * An organisation's monthly recurring revenue (ZAR), read from the AUTHORITATIVE
 * source for its payment channel:
 *   - a paid subscription → the live subscription price (`subscriptionMonthlyZAR`,
 *     joined backend-side from the org-subscriptions record), falling back to the
 *     admin-set `monthlyAmountZAR` only when the price didn't join (older backend);
 *   - a direct EFT payer → the admin-set `monthlyAmountZAR` (EFT has no Stitch
 *     subscription, so this field IS its revenue — stamped at confirm-EFT time);
 *   - anything else → 0.
 *
 * Non-paying orgs contribute 0, so summing this over any org set yields the
 * correct MRR without depending on the sparsely-filled `monthlyAmountZAR`. When
 * both channels are set (belt-and-braces data state) the subscription price wins,
 * matching the channel label the commission + breakdown assign.
 */
export function monthlyRevenueZar(org: Organization): number {
  if (org.subscriptionTag === 'paid') {
    return org.subscriptionMonthlyZAR ?? org.monthlyAmountZAR ?? 0;
  }
  if (org.eftPayer === true) {
    return org.monthlyAmountZAR ?? 0;
  }
  return 0;
}

/**
 * The billing signals a channel predicate needs. A MINIMAL structural shape (the
 * same reasoning as `BillingTagChip`'s `BillingSignals`) so an Organizations row
 * AND a Usage Health row both satisfy it; `Organization` satisfies it too, so
 * every existing caller passing a full org is unchanged.
 */
export type OrgBillingSignals = Pick<
  Organization,
  'subscriptionTag' | 'eftPayer' | 'billingMode'
>;

/**
 * The org pays through a live self-serve Stitch subscription. This is the SAME
 * `subscriptionTag === 'paid'` signal `isPayingOrg` reads — named here so a
 * caller that needs the CHANNEL (not just "is it paying") doesn't re-roll the
 * comparison. Trello #363.
 */
export function isSubscribingOrg(org: OrgBillingSignals): boolean {
  return org.subscriptionTag === 'paid';
}

/**
 * The org pays by direct EFT into the bank account (admin-set marker). An EFT
 * payer has no Stitch subscription, so its `subscriptionTag` joins to 'unpaid' —
 * which is exactly why EFT needs its own predicate rather than being inferred
 * from the tag (Trello #332 / #377).
 */
export function isEftOrg(org: OrgBillingSignals): boolean {
  return org.eftPayer === true;
}

/**
 * The org is on a MANAGED (comp'd / invoiced-by-us) plan rather than self-serve —
 * true when EITHER managed signal is set: the admin-set `billingMode` or the
 * backend-joined `subscriptionTag`.
 *
 * Deliberately an OR, not the ORDERED cascade in
 * `BillingTagChip.effectiveBillingTag` (which must pick exactly ONE tag to draw,
 * so it prefers the live subscription tag and only falls through to
 * `billingMode`). Here the question is membership, not display: an org whose tag
 * joined to 'trial' but whose billingMode says 'managed' still belongs in the
 * "Managed organisations" filter. Lives in this pure module so a non-React
 * caller (the Bulk Invoices filter) can use it without pulling in MUI.
 */
export function isManagedOrg(org: OrgBillingSignals): boolean {
  return org.billingMode === 'managed' || org.subscriptionTag === 'managed';
}

/** The payment channel a paying org's revenue arrives through. */
export type PaymentChannel = 'subscription' | 'eft';

/**
 * The channel a PAYING org pays through. Precondition: `isPayingOrg(org)` is true
 * — so a non-'paid' org here is guaranteed to be an EFT payer. Subscription wins
 * when both markers are set. Do NOT call on a non-paying org (it would mislabel).
 */
export function paymentChannel(org: Organization): PaymentChannel {
  return isSubscribingOrg(org) ? 'subscription' : 'eft';
}

/**
 * The two audience labels shared by every "who am I addressing?" control in the
 * dashboard.
 *
 * They live here, beside the predicates, because two controls in one app that
 * both say "Paying organisations" must not be able to resolve — or read — as
 * different things. The Bulk Invoices "Show" filter (Trello #363) and the
 * announcement-email audience option (Trello #395) both compose these rather
 * than each spelling the strings out.
 *
 * The first two are shared: the Bulk Invoices "Show" filter uses them too.
 * The third is announcement-email-only — nothing else in the dashboard lets you
 * hand-pick an audience — but it lives here anyway, because splitting one radio
 * group's labels across two files is the drift this block exists to prevent.
 *
 * Still deliberately NOT here: `subscribing` / `eft` / `managed` are Bulk
 * Invoices' payment-CHANNEL breakdown, which is a different question from the
 * audience one, and they stay local to that page.
 */
export const ORG_AUDIENCE_ALL_LABEL = 'All organisations';
export const ORG_AUDIENCE_PAYING_LABEL = 'Paying organisations';
export const ORG_AUDIENCE_SELECTED_LABEL = 'Selected organisations';
